import { useMemberPoliciesQuery } from '@/api/query/policy';
import { useMemberQuery } from '@/api/query/user';
import type { Member, MemberBalanceSummary } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import { useDrawers, type DrawerKind } from '@/contexts/DrawerContext';
import { ViewMember } from '@/pages/members/ViewMember';
import { UpsertMember } from '@/pages/members/UpsertMember';
import { UpsertPolicy } from '@/pages/policies/UpsertPolicy';
import { PolicySavings } from '@/pages/policies/PolicySavings';
import { PolicyDetails } from '@/pages/payments/PolicyDetails';
import { UpsertPayment } from '@/pages/payments/UpsertPayment';
import { PolicyLoan } from '@/pages/loans/PolicyLoan';
import { MemberBalanceDrawer } from '@/pages/balances/MemberBalanceDrawer';
import { MemberTransactionsModal } from '@/pages/members/MemberTransactionsModal';

/**
 * Every member / policy / loan / balance drawer (and the Transaction History popup), rendered once for the whole app.
 * Open one with useDrawers().openDrawer(...) from any page, table cell or drawer.
 */
export function AppDrawers() {
  const { isAuthenticated } = useAppContext();
  const { target, openDrawer, closeDrawer } = useDrawers();
  const memberId = target?.member.id;
  // View / Edit Member need the full member record; every other drawer needs the member's policies.
  const isMemberDrawer = target?.kind === 'viewMember' || target?.kind === 'editMember';
  const needsPolicies = Boolean(target) && !isMemberDrawer && target?.kind !== 'memberTransactions';
  const { data: fullMember } = useMemberQuery(memberId, isAuthenticated && isMemberDrawer);
  const { data: policies = [] } = useMemberPoliciesQuery(memberId, isAuthenticated && needsPolicies);

  // Id and name always come from the clicked row; View / Edit Member add the full record.
  const member: Member | null = target
    ? {
        phone: '',
        joinedDate: '',
        ...(isMemberDrawer ? fullMember : undefined),
        id: target.member.id,
        name: target.member.name,
        memberNo: target.member.memberNo ?? fullMember?.memberNo,
      }
    : null;
  // Buttons pass the policy id; policy / loan number links pass the policy number.
  const policy =
    policies.find((item) => item.id === target?.policyId || item.policyNo === target?.policyNo) ?? null;
  // Every policy carries the same member balance due.
  const balanceDue = policies[0]?.memberBalanceDue ?? 0;
  const balance: MemberBalanceSummary = { balanceDue, interestDue: 0, hasHistory: balanceDue > 0 };

  const isOpen = (kind: DrawerKind) => target?.kind === kind;
  const handleOpenChange = (open: boolean) => {
    if (!open) closeDrawer();
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <ViewMember
        member={member}
        open={isOpen('viewMember')}
        onOpenChange={handleOpenChange}
        onEdit={(item) => openDrawer('editMember', item)}
      />
      <UpsertMember member={member} open={isOpen('editMember')} onOpenChange={handleOpenChange} />
      <UpsertPolicy member={member} policies={policies} open={isOpen('addPolicy')} onOpenChange={handleOpenChange} />
      <UpsertPolicy
        member={member}
        policies={policies}
        policy={policy}
        open={isOpen('editPolicy')}
        onOpenChange={handleOpenChange}
      />
      <PolicyDetails
        member={member}
        policies={policies}
        balance={balance}
        initialPolicyId={policy?.id ?? target?.policyId}
        open={isOpen('policyDetails')}
        onOpenChange={handleOpenChange}
      />
      <UpsertPayment
        member={member}
        policy={policy}
        balanceDue={balanceDue}
        open={isOpen('policyPayment')}
        onOpenChange={handleOpenChange}
      />
      <PolicyLoan member={member} policy={policy} balanceDue={balanceDue} open={isOpen('policyLoan')} onOpenChange={handleOpenChange} />
      <PolicySavings member={member} policy={policy} open={isOpen('policySavings')} onOpenChange={handleOpenChange} />
      <MemberBalanceDrawer member={member} open={isOpen('memberBalance')} onOpenChange={handleOpenChange} />
      <MemberTransactionsModal member={member} open={isOpen('memberTransactions')} onOpenChange={handleOpenChange} />
    </>
  );
}
